import { storeToRefs } from "pinia";
import { computed, isReactive, isRef, ref, toRef, type Ref } from "vue";
import { useDemandStore } from "@/stores/demandStore";
import { useProjectStore } from "@/stores/projectStore";
import { useServiceStore } from "@/stores/serviceStore";
import { useComparatorStore } from "@/stores/comparatorStore";
import { useOfferStore } from "@/stores/offerStore";
import { useConfigStore } from "@/stores/configStore";
import type { Offer } from "@/types/Offers";

export interface useMoveManagerOverrides {
	onStripeError?: (errorCode: string) => void;
	forceReloadOnCreate?: boolean;
	isDisplayMoverPhotos?: boolean;
	forceGetOffer?: boolean;
	checkAuthorization?: () => boolean;
}

export interface useMoveManagerEmits {
	emit: (event: string, ...args: unknown[]) => void;
}

export type MoveManagerOfferSource = Offer | null | Ref<Offer | null>;
export type MoveManagerProps = { offer: MoveManagerOfferSource } | undefined;

export function getMoveManagerOffer(props?: MoveManagerProps) {
	const localOffer = ref<Offer | null>(null);

	// Support:
	// - Vue component props (reactive object) => toRef(props, "offer")
	// - pinia/store refs => props.offer is a Ref
	// - plain objects / snapshots => fallback local ref
	const offer: Ref<Offer | null> = (() => {
		const raw = props?.offer;
		if (isRef(raw)) return raw;
		if (props && isReactive(props)) return toRef(props as { offer: Offer | null }, "offer");
		localOffer.value = (raw ?? null) as Offer | null;
		return localOffer;
	})();

	const currentOffer = computed(() => offer.value);
	return {
		offer,
		currentOffer,
	};
}

export function getMoveManagerStore() {
	const demandStore = useDemandStore();
	const projectStore = useProjectStore();
	const serviceStore = useServiceStore();
	const comparatorStore = useComparatorStore();
	const offerStore = useOfferStore();
	const configStore = useConfigStore();

	const { demand } = storeToRefs(demandStore);
	const { nbDaysBookingLimit, projectSelected } = storeToRefs(projectStore);
	const { servicesPrice, services: servicesStore } = storeToRefs(serviceStore);
	const { offers, offersLoading } = storeToRefs(offerStore);
	const { prebookDate } = storeToRefs(comparatorStore);

	return {
		demandStore,
		projectStore,
		serviceStore,
		comparatorStore,
		offerStore,
		configStore,
		demand,
		nbDaysBookingLimit,
		projectSelected,
		servicesPrice,
		servicesStore,
		offers,
		offersLoading,
		prebookDate,
	};
}

export function createMoveManagerContext(props?: MoveManagerProps) {
	return {
		...getMoveManagerOffer(props),
		...getMoveManagerStore(),
	};
}

export type MoveManagerContext = ReturnType<typeof createMoveManagerContext>;

